import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { FoxSpinnerInline } from '../ui/FoxSpinner';

const ClientHostWorkEvent = ({ kakoId }) => {
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableRewards, setAvailableRewards] = useState([]);
  const [myHistory, setMyHistory] = useState([]);
  const [claiming, setClaiming] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    if (kakoId) {
      loadEventData();
      loadAvailableRewards();
      loadMyHistory();
      checkIfBlocked();
    }
  }, [kakoId]);

  const checkIfBlocked = async () => {
    try {
      const response = await api.get(`/agency/exclusions/check/${kakoId}`);
      const data = response.data;
      
      if (data.is_in_list && data.excluded_from.trabalhou_ganhou) {
        setIsBlocked(true);
      }
    } catch (error) {
      console.error('Erro ao verificar bloqueio:', error);
    }
  };

  const loadEventData = async () => {
    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/host-work-event/my-data/${kakoId}`);
      console.log('💼 Dados do evento carregados:', response.data);
      setEventData(response.data);
    } catch (error) {
      console.error('❌ Erro ao carregar dados do evento:', error);
      setEventData(null);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableRewards = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/host-work-event/my-rewards/${kakoId}`);
      setAvailableRewards(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar recompensas:', error);
    }
  };

  const loadMyHistory = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/host-work-event/my-history/${kakoId}`);
      setMyHistory(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  const handleClaimReward = async (weekStart) => {
    setClaiming(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${BACKEND_URL}/api/host-work-event/claim-reward`, null, {
        params: {
          show_id: kakoId,
          week_start: weekStart
        }
      });
      
      toast.success(response.data.message);
      await loadAvailableRewards();
      await loadEventData();
      await loadMyHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resgatar moedas');
    } finally {
      setClaiming(false);
    }
  };

  console.log('💼 ClientHostWorkEvent renderizando:', { kakoId, eventData, loading });

  if (loading) {
    console.log('💼 Mostrando loading...');
    return (
      <div className="flex justify-center p-8">
        <FoxSpinnerInline size="lg" />
      </div>
    );
  }

  if (!eventData) {
    console.log('💼 eventData é null');
    return (
      <Card>
        <CardContent className="p-8 text-center text-gray-500">
          <p>Carregando dados do evento...</p>
        </CardContent>
      </Card>
    );
  }

  // MENSAGEM DE BLOQUEIO (verificar primeiro!)
  if (isBlocked || eventData.blocked) {
    console.log('💼 Usuário bloqueado do evento');
    return (
      <Card className="border-2 border-red-500">
        <CardContent className="p-6 sm:p-8 text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h3 className="text-xl sm:text-2xl font-bold text-red-600 mb-3">Acesso Bloqueado</h3>
          <p className="text-sm sm:text-base text-gray-700 mb-2">
            Você está bloqueado de participar deste evento por não cumprir requisitos ou descumprir termos e regras.
          </p>
          <p className="text-xs sm:text-sm text-gray-500 mt-4">
            Para mais informações, entre em contato com a administração da agência.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!eventData.active) {
    console.log('💼 Evento não está ativo (eventData.active = false)');
    return (
      <Card>
        <CardContent className="p-8 text-center text-gray-500">
          <p>Evento não está ativo no momento</p>
        </CardContent>
      </Card>
    );
  }

  console.log('💼 Renderizando conteúdo completo...');

  const { total_points, valid_days, multiplier, crystals_estimated, is_eligible, daily_points } = eventData;

  return (
    <div className="space-y-4">
      {/* Header do Evento */}
      <Card 
        className="text-white overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_7f473fba-bcad-429d-a8e1-d860614a1332/artifacts/unotjo4t_ChatGPT%20Image%205%20de%20fev.%20de%202026%2C%2019_20_13.png')"
        }}
      >
        <CardHeader className="relative">
          <div className="flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_7f473fba-bcad-429d-a8e1-d860614a1332/artifacts/prj5vls6_trabalhou.png" 
              alt="TRABALHOU, GANHOU!" 
              className="h-24 sm:h-32 object-contain relative z-10"
            />
          </div>
          <p className="text-sm text-center text-white drop-shadow-lg mt-2 relative z-10">Semana: {eventData.week_start} a {eventData.week_end}</p>
        </CardHeader>
      </Card>

      {/* Tabs: Semana Atual, Semana Anterior, Regras, Histórico */}
      <Tabs defaultValue="dados" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="dados" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-xs sm:text-sm">
            📊 Semana Atual
          </TabsTrigger>
          <TabsTrigger value="semana-anterior" className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-xs sm:text-sm">
            📅 Semana Anterior
          </TabsTrigger>
          <TabsTrigger value="regras" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-xs sm:text-sm">
            📋 Regras
          </TabsTrigger>
          <TabsTrigger value="historico" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-xs sm:text-sm">
            📜 Histórico
          </TabsTrigger>
        </TabsList>

        {/* Sub-aba: Semana Atual (Dados) */}
        <TabsContent 
          value="dados" 
          className="mt-4 space-y-4 p-6 rounded-xl bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_7f473fba-bcad-429d-a8e1-d860614a1332/artifacts/fx1fvpob_ChatGPT%20Image%205%20de%20fev.%20de%202026%2C%2019_20_17.png')"
          }}
        >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-300">
          <CardContent className="p-4">
            <p className="text-xs text-gray-600 mb-1">Pontos Acumulados</p>
            <p className="text-2xl font-bold text-yellow-700">{total_points.toFixed(1)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-300">
          <CardContent className="p-4">
            <p className="text-xs text-gray-600 mb-1">Dias Válidos</p>
            <p className="text-2xl font-bold text-blue-700">{valid_days}/7</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-300">
          <CardContent className="p-4">
            <p className="text-xs text-gray-600 mb-1">Multiplicador</p>
            <p className="text-2xl font-bold text-purple-700">x{multiplier}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-300">
          <CardContent className="p-4">
            <p className="text-xs text-gray-600 mb-1">Moedas Estimadas</p>
            <p className="text-xl font-bold text-green-700">{crystals_estimated.toLocaleString('pt-BR')} 🪙</p>
          </CardContent>
        </Card>
      </div>

      {/* Status de Elegibilidade */}
      {is_eligible ? (
        <div className="p-4 bg-green-50 border-2 border-green-400 rounded-lg text-center">
          <p className="text-green-800 font-bold">✅ Você está elegível para receber a recompensa!</p>
          <p className="text-xs text-green-700 mt-1">Continue trabalhando para aumentar suas moedas!</p>
        </div>
      ) : (
        <div className="p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg text-center">
          <p className="text-yellow-800 font-bold">⚠️ Mínimo 4 dias necessários</p>
          <p className="text-xs text-yellow-700 mt-1">Você tem {valid_days} dia(s) válido(s). Faltam {4 - valid_days} dia(s).</p>
        </div>
      )}
        </TabsContent>

        {/* Sub-aba: Semana Anterior (com Recompensas) */}
        <TabsContent value="semana-anterior" className="mt-4">
          <Card className="overflow-hidden bg-cover bg-center bg-no-repeat" style={{backgroundImage: "url('https://customer-assets.emergentagent.com/job_7f473fba-bcad-429d-a8e1-d860614a1332/artifacts/fx1fvpob_ChatGPT%20Image%205%20de%20fev.%20de%202026%2C%2019_20_17.png')"}}>
            <CardHeader className="relative bg-white/80 backdrop-blur-sm pb-3">
              <CardTitle className="text-base sm:text-lg">📅 Semana Anterior - Resgatar Recompensa</CardTitle>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Resgate suas moedas da semana que encerrou</p>
            </CardHeader>
            <CardContent className="relative bg-white/90 backdrop-blur-sm">
              {availableRewards.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-3">💰</div>
                  <p className="text-gray-500 text-sm">Nenhuma recompensa disponível no momento</p>
                  <p className="text-xs text-gray-400 mt-2">As recompensas ficam disponíveis toda segunda-feira às 09:00</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {availableRewards.map((reward) => (
                    <Card key={reward.id} className="border-2 border-green-400 bg-gradient-to-r from-green-50 to-emerald-50">
                      <CardContent className="p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-3">
                          <div className="flex-1">
                            <p className="font-bold text-sm sm:text-base text-gray-900">
                              🏆 Recompensa da Semana
                            </p>
                            <p className="text-xs sm:text-sm text-gray-600">
                              Semana: {reward.week_start} a {reward.week_end}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {reward.valid_days} dias válidos • {reward.total_points.toFixed(1)} pontos • x{reward.multiplier}
                            </p>
                            <p className="text-xs text-orange-600 mt-1">
                              ⏰ Expira: {new Date(reward.expires_at).toLocaleDateString('pt-BR')} às {new Date(reward.expires_at).toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})}
                            </p>
                          </div>
                          <div className="text-left sm:text-right">
                            <p className="text-2xl sm:text-3xl font-bold text-green-600">
                              {reward.crystals_earned.toLocaleString('pt-BR')} 💎
                            </p>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleClaimReward(reward.week_start)}
                          disabled={claiming}
                          className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-sm sm:text-base py-2 sm:py-3"
                        >
                          {claiming ? 'Resgatando...' : '💰 Resgatar Moedas Agora'}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sub-aba: Regras */}
        <TabsContent 
          value="regras" 
          className="mt-4 p-6 rounded-xl bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_7f473fba-bcad-429d-a8e1-d860614a1332/artifacts/fx1fvpob_ChatGPT%20Image%205%20de%20fev.%20de%202026%2C%2019_20_17.png')"
          }}
        >
          <Card className="bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200">
            <CardHeader>
              <CardTitle className="text-base sm:text-lg text-purple-900">📋 Regras do Evento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p className="text-gray-700">
                As quantidades de pontos obtidos são determinadas pelo valor do presente da sorte recebido do dia e a duração de live.
              </p>
              
              {/* Tabela de Pontos Diários */}
              <div>
                <h3 className="font-bold text-purple-800 mb-2">Tabela de Pontos Diários:</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-purple-600 text-white">
                        <th className="p-2 text-left border border-purple-400">Meta concluída do dia</th>
                        <th className="p-2 text-center border border-purple-400">Duração de live do dia</th>
                        <th className="p-2 text-center border border-purple-400">Pontos obtidos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[[0, '2H', 0.3], [150000, '2H', 0.4], [300000, '2H', 0.5], [600000, '2H', 1], [1000000, '2H', 2], [1500000, '2H', 3], [3000000, '2H', 4], [5000000, '2H', 5], [8000000, '2H', 6], [12000000, '2H', 7], [20000000, '2H', 8], [30000000, '2H', 9], [50000000, '2H', 10], [100000000, '2H', 11]].map((row, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-purple-50'}>
                          <td className="p-2 border border-gray-300">{row[0].toLocaleString('pt-BR')}</td>
                          <td className="p-2 text-center border border-gray-300">{row[1]}</td>
                          <td className="p-2 text-center border border-gray-300 text-purple-700 font-bold">{row[2]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <p className="text-gray-600 italic">No final, os pontos obtidos serão determinados pelo número de dias de presença na semana.</p>
              
              {/* Tabela de Presença Semanal */}
              <div>
                <h3 className="font-bold text-purple-800 mb-2">Presença Semanal:</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-purple-600 text-white">
                        <th className="p-2 text-left border border-purple-400">Presença da semana</th>
                        <th className="p-2 text-center border border-purple-400">Múltiplo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[['4 dias', 'x1'], ['5 dias', 'x1.5'], ['6 dias', 'x1.7'], ['7 dias', 'x2']].map((row, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-purple-50'}>
                          <td className="p-2 border border-gray-300">{row[0]}</td>
                          <td className="p-2 text-center border border-gray-300 text-purple-700 font-bold">{row[1]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Sobre os pontos */}
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <h3 className="font-bold text-blue-800 mb-1">💎 Sobre os pontos:</h3>
                <p>Cada ponto pode ser trocado por <span className="font-bold text-blue-900">2000 moedas</span></p>
                <p className="text-xs mt-1">Moedas ganhas = Pontos × Múltiplo × 2000</p>
              </div>
              
              {/* Sobre o evento */}
              <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                <h3 className="font-bold text-yellow-800 mb-2">⚠️ Sobre o evento:</h3>
                <ol className="text-xs space-y-1 pl-4">
                  <li>1. Os pontos são válidos apenas na mesma semana.</li>
                  <li>2. É necessário ter, no mínimo, 4 dias de presença na semana para receber a recompensa.</li>
                  <li>3. Cada semana natural tem um ciclo de evento completo.</li>
                  <li>4. Tempo de pagamento: Após o término de cada semana, o pagamento pode ser coletado manualmente após as 09:00 do primeiro dia útil da semana seguinte.</li>
                  <li>5. Caso seja detectado live com ausência do host, live com tela preta e etc., o direito de participar do evento será cancelado.</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sub-aba: Histórico */}
        <TabsContent value="historico" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">📜 Meu Histórico de Resgates</CardTitle>
            </CardHeader>
            <CardContent>
              {myHistory.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Você ainda não resgatou nenhuma recompensa</p>
              ) : (
                <div className="space-y-3">
                  {myHistory.map((reward) => (
                    <div key={reward.id} className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-bold text-gray-900">Semana: {reward.week_start} a {reward.week_end}</p>
                          <p className="text-sm text-gray-600">
                            {reward.valid_days} dias • {reward.total_points.toFixed(1)} pontos • x{reward.multiplier}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Resgatado em: {new Date(reward.claimed_at).toLocaleDateString('pt-BR')} às {new Date(reward.claimed_at).toLocaleTimeString('pt-BR')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-600">
                            {reward.crystals_earned.toLocaleString('pt-BR')} 💎
                          </p>
                          <Badge className="bg-green-600 mt-1">✓ Resgatado</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClientHostWorkEvent;
